'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';
import { Settings, User } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { useDeviceType } from '@/hooks/useIsMobile';
import { useThemeColor } from '@/hooks/useThemeColor';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalSlideStore } from '@/stores/useModalSlideStore';
import { SERVICE_ITEMS } from '@/constants/constants';
import { ServiceItem } from '@/types/services';
import { UserItem } from '@/types/users';
import IconAdaptive from '@/components/IconAdaptive';
import Popover from '@/components/modal/Popover';
import HeaderSlideMenu from '@/components/modal/features/HeaderSlideMenu';
import UserAvatar from '@/components/ui/avatar/UserAvatar';

type SubItemSection = {
    label: string;
    jp: string;
    Icon: any;
    items: { label: string; jp: string; path: string }[];
};

type ActiveSubItemsPopoverState = {
    label: string;
    subItems: SubItemSection[];
    position: { top: number; left: number };
} | null;

const POPOVER_OFFSET_Y = 30;

const SETTINGS_ITEM: ServiceItem = {
    label: 'MENU.',
    jp: 'メニュー',
    path: '',
    match: [],
    Icon: Settings,
    subItems: [],
};

export default function MobileFooter() {
    const { scheme } = useThemeColor();
    const router = useRouter();
    const pathname = usePathname();
    const openModalSlide = useModalSlideStore((state) => state.openModalSlide);
    const isModalOpen = useModalSlideStore((s) => s.slides.length > 0);
    const { isTablet } = useDeviceType();

    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const resetAuth = useAuthStore((s) => s.resetAuth);
    const { handleError } = useHandleError();

    const [user, setUser] = useState<UserItem>();
    const [isRadialOpen, setIsRadialOpen] = useState(false);
    const [isBlurActive, setIsBlurActive] = useState(false);
    const [isFooterVisible, setIsFooterVisible] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const [activeSubItemsPopover, setActiveSubItemsPopover] = useState<ActiveSubItemsPopoverState>(null);

    const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);

    const setItemRef = useCallback(
        (label: string) => (el: HTMLButtonElement | null) => {
            itemRefs.current[label] = el;
        },
        [],
    );

    useEffect(() => {
        if (!isAuthenticated) {
            setUser(undefined);
            return;
        }

        (async () => {
            try {
                const data = await apiFetch('/users/me', { method: 'GET' });
                if (!data.user.schoomyers_id) {
                    resetAuth();
                    setUser(undefined);
                } else {
                    setUser(data.user);
                }
            } catch (error: unknown) {
                handleError(error);
            }
        })();
    }, [isAuthenticated, resetAuth]);

    useEffect(() => {
        if (!activeSubItemsPopover) return;

        const handleResize = () => {
            const el = itemRefs.current[activeSubItemsPopover.label];
            if (!el) return;
            const rect = el.getBoundingClientRect();
            setActiveSubItemsPopover((prev) =>
                prev
                    ? {
                          ...prev,
                          position: {
                              top: rect.bottom + POPOVER_OFFSET_Y,
                              left: rect.left + rect.width / 2,
                          },
                      }
                    : null,
            );
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeSubItemsPopover]);

    useEffect(() => {
        if (!isModalOpen) {
            setIsBlurActive(false);
        }
    }, [isModalOpen]);

    useEffect(() => {
        let observer: IntersectionObserver | null = null;

        const observe = () => {
            const el = document.getElementById('footer-copyright');
            if (!el) return false;

            observer = new IntersectionObserver(([entry]) => setIsFooterVisible(entry.isIntersecting), {
                threshold: 0.1,
            });
            observer.observe(el);
            return true;
        };

        if (observe()) return;

        const mutation = new MutationObserver(() => {
            if (observe()) mutation.disconnect();
        });
        mutation.observe(document.body, { childList: true, subtree: true });
        return () => {
            mutation.disconnect();
            observer?.disconnect();
        };
    }, []);

    useEffect(() => {
        let scrollEl: Window | Element = window;

        const handleScroll = () => {
            if (isBlurActive) return;
            setIsScrolling(true);
            if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
            scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 300);
        };

        const getScrollEl = (): Window | Element | null => {
            const footer = document.getElementById('footer-copyright');
            if (!footer) return null;
            let el: Element | null = footer.parentElement;
            while (el) {
                const overflow = getComputedStyle(el).overflowY;
                if (overflow === 'auto' || overflow === 'scroll') return el;
                el = el.parentElement;
            }
            return window;
        };

        const attach = () => {
            const next = getScrollEl();
            if (!next) return false;
            scrollEl.removeEventListener('scroll', handleScroll);
            scrollEl = next;
            scrollEl.addEventListener('scroll', handleScroll, { passive: true });
            return true;
        };

        let mutationObs: MutationObserver | null = null;

        if (!attach()) {
            mutationObs = new MutationObserver(() => {
                if (attach()) mutationObs?.disconnect();
            });
            mutationObs.observe(document.body, { childList: true, subtree: true });
        }

        return () => {
            scrollEl.removeEventListener('scroll', handleScroll);
            mutationObs?.disconnect();
            if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
        };
    }, [isBlurActive]);

    const RADIAL_OFFSETS = isTablet ? [-230, -120, 120, 230] : [-140, -80, 80, 140];
    const mobileItems = SERVICE_ITEMS.filter((i) => i.mobileVisible !== false);
    const radialItems: ServiceItem[] = [SETTINGS_ITEM, ...mobileItems];

    const handleClickRadialItem = (item: ServiceItem) => {
        setIsBlurActive(true);

        if (item.label === SETTINGS_ITEM.label) {
            setIsRadialOpen(false);
            openModalSlide({
                id: 'footer_menu_mobile',
                isHeaderLog: true,
                component: <HeaderSlideMenu />,
                size: { pcWidth: '100%', mbWidth: '100%', height: '100%' },
                direction: 'down',
            });
            return;
        }

        const hasSubItems = item.subItems && item.subItems.length > 0;
        if (hasSubItems) {
            const fab = itemRefs.current['__fab__'];
            if (!fab) return;
            const rect = fab.getBoundingClientRect();
            setActiveSubItemsPopover({
                label: item.label,
                subItems: item.subItems as SubItemSection[],
                position: {
                    top: rect.top + POPOVER_OFFSET_Y,
                    left: rect.left + rect.width / 2,
                },
            });
        } else {
            setIsBlurActive(false);
            setIsRadialOpen(false);
            router.push(item.path);
        }
    };

    const handleClosePopover = () => {
        setActiveSubItemsPopover(null);
        setIsBlurActive(false);
    };

    const handleNavigatePopover = (path: string) => {
        router.push(path);
        setActiveSubItemsPopover(null);
        setIsBlurActive(false);
        setIsRadialOpen(false);
    };

    const renderCenterContent = () => {
        if (!isAuthenticated) {
            return <IconAdaptive Icon={User} baseSize={28} strokeWidth={2.0} className="text-color-white-base" />;
        }
        if (isAuthenticated && !user) {
            return null;
        }
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3, duration: 0.5 }}
                className="bg-color-white-base rounded-full"
            >
                <UserAvatar
                    avatarUrl={user?.avatar_resource_url}
                    schoomyers_id={user?.schoomyers_id}
                    size={65}
                    breakpoints={{ md: 65 }}
                    borderColorClassName="border-color-white-base"
                />
            </motion.div>
        );
    };

    return (
        <>
            <AnimatePresence>
                {isBlurActive && (
                    <motion.div
                        key="blur-overlay"
                        className="fixed inset-0 z-[1998]"
                        style={{
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            background: 'rgba(0,0,0,0.55)',
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => {
                            setIsBlurActive(false);
                            setIsRadialOpen(false);
                            setActiveSubItemsPopover(null);
                        }}
                    />
                )}
            </AnimatePresence>

            <div
                className="fixed inset-x-0 bottom-0 z-[2000] flex items-end justify-center pointer-events-none"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                <div className="relative flex items-center justify-center h-[80px] w-full">
                    {radialItems.map((item, i) => {
                        const offset = RADIAL_OFFSETS[i];
                        const isActive = item.match?.some((m) => pathname.startsWith(m));
                        return (
                            <motion.button
                                key={item.label}
                                type="button"
                                ref={setItemRef(item.label)}
                                style={{
                                    width: isTablet ? 60 : 40,
                                    height: isTablet ? 60 : 40,
                                    bottom: 14,
                                    backdropFilter: 'blur(12px)',
                                    WebkitBackdropFilter: 'blur(12px)',
                                }}
                                className={`absolute flex items-center justify-center rounded-full cursor-pointer pointer-events-auto border-[4px] ${scheme?.bgBorder} ${scheme?.bg}`}
                                initial={{ x: 0, opacity: 0, scale: 0.4 }}
                                animate={
                                    isRadialOpen && !isFooterVisible
                                        ? { x: offset, opacity: 1, scale: 1 }
                                        : { x: 0, opacity: 0, scale: 0.4 }
                                }
                                transition={{
                                    type: 'spring',
                                    stiffness: 400,
                                    damping: 28,
                                    delay: isRadialOpen ? i * 0.05 : (radialItems.length - 1 - i) * 0.03,
                                }}
                                onClick={() => handleClickRadialItem(item)}
                            >
                                <item.Icon
                                    size={isTablet ? 26 : 20}
                                    strokeWidth={2}
                                    className={isActive ? 'text-color-service-selected' : 'text-color-white-base'}
                                />
                            </motion.button>
                        );
                    })}

                    <motion.button
                        type="button"
                        ref={setItemRef('__fab__')}
                        whileTap={{ scale: 0.9 }}
                        style={{
                            WebkitTapHighlightColor: 'transparent',
                            width: 65,
                            height: 65,
                            bottom: 10,
                            zIndex: 1,
                        }}
                        className={`absolute flex items-center justify-center rounded-full cursor-pointer pointer-events-auto border-[6px] ${scheme?.bgBorder} ${scheme?.bg}`}
                        animate={{
                            opacity: isFooterVisible || (isScrolling && !isRadialOpen) ? 0 : 1,
                            y: isFooterVisible || (isScrolling && !isRadialOpen) ? 20 : 0,
                        }}
                        transition={{ duration: 0.3 }}
                        onClick={() => setIsRadialOpen((v) => !v)}
                    >
                        {renderCenterContent()}
                    </motion.button>
                </div>
            </div>

            {activeSubItemsPopover && (
                <Popover
                    label={activeSubItemsPopover.label}
                    sections={activeSubItemsPopover.subItems}
                    direction="up"
                    isCentered={true}
                    position={activeSubItemsPopover.position}
                    onNavigate={handleNavigatePopover}
                    onClose={handleClosePopover}
                />
            )}
        </>
    );
}
